import React, { useState, useEffect, useRef } from 'react'
import {
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
  Paper,
  Box,
  CircularProgress,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import { useUploadStatus } from 'src/components/UploadStatusIndicator/UploadStatusContext'
import IconButton from '../MUI/IconButton'

const UploadStatusIndicator: React.FC = () => {
  const { uploads, removeUpload, hasActiveUploads, clearCompleted } =
    useUploadStatus()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleClose = () => {
    if (!hasActiveUploads) {
      clearCompleted()
      setOpen(false)
    }
  }

  const handleRemoveUpload = (id: string) => {
    removeUpload(id)
  }

  // Auto-open the popover when uploads start
  useEffect(() => {
    if (hasActiveUploads) {
      setOpen(true)
    } else if (Object.keys(uploads).length === 0) {
      // Close the popover when all uploads are removed
      setOpen(false)
    }
  }, [hasActiveUploads, uploads])

  // Count active uploads
  const activeUploads = Object.values(uploads).filter(
    (upload) => upload.status === 'uploading' || upload.status === 'ingesting'
  ).length

  const totalUploads = Object.keys(uploads).length

  // If no uploads, don't render anything
  if (totalUploads === 0) return null

  return (
    <div ref={containerRef}>
      <Popover
        open={open}
        anchorEl={containerRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
              borderRadius: '8px',
            },
          },
        }}
      >
        <Paper sx={{ width: 500, maxHeight: 400, overflow: 'auto' }}>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #eee',
            }}
          >
            <Typography variant="h6">
              File Uploads {activeUploads > 0 && `(${activeUploads} active)`}
            </Typography>
            {!hasActiveUploads && (
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <List sx={{ pt: 0 }}>
            {Object.values(uploads).map((upload) => (
              <ListItem
                key={upload.id}
                sx={{
                  borderBottom: '1px solid #f5f5f5',
                  bgcolor: upload.status === 'error' ? '#fff8f8' : 'inherit',
                }}
              >
                {['uploading', 'ingesting'].includes(upload.status) && (
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                )}
                {upload.status === 'success' && (
                  <CheckCircleIcon
                    color="success"
                    fontSize="small"
                    sx={{ mr: 2 }}
                  />
                )}
                {upload.status === 'error' && (
                  <ErrorIcon color="error" fontSize="small" sx={{ mr: 2 }} />
                )}
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color:
                          upload.status === 'error' ? 'error.main' : 'inherit',
                      }}
                    >
                      {upload.filename}
                    </Typography>
                  }
                  secondary={
                    <>
                      {upload.status === 'uploading' && (
                        <Typography variant="caption" display="block">
                          Uploading
                        </Typography>
                      )}
                      {upload.status === 'ingesting' && (
                        <Typography variant="caption" display="block">
                          Ingesting into DocHub
                        </Typography>
                      )}
                      {upload.status === 'error' && (
                        <Typography variant="caption" color="error">
                          {`Upload failed${
                            upload.error ? `: ${upload.error}` : ''
                          }`}
                        </Typography>
                      )}
                      {upload.status === 'success' && (
                        <Typography variant="caption" color="success.main">
                          {upload.attached ? 'Attached' : 'Upload complete'}
                        </Typography>
                      )}
                    </>
                  }
                />
                {/* Only show delete button for completed or error uploads */}
                {(upload.status === 'success' || upload.status === 'error') && (
                  <IconButton
                    aria-label="delete"
                    onClick={() => handleRemoveUpload(upload.id)}
                    size="small"
                    tooltip="Clear"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </ListItem>
            ))}
          </List>
        </Paper>
      </Popover>
    </div>
  )
}

export default UploadStatusIndicator
